import React, { useState } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { LogIn, Lock, Mail, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('Lütfen e-posta ve şifrenizi girin.');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password' || err.code === 'auth/user-not-found') {
        setError('E-posta adresi veya şifre hatalı.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Geçersiz bir e-posta adresi girdiniz.');
      } else {
        setError('Giriş yapılırken bir hata oluştu: ' + (err.message || err.code));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) {
      setError('Şifre sıfırlama e-postası göndermek için lütfen önce e-posta adresinizi girin.');
      setSuccessMessage(null);
      return;
    }
    setResetLoading(true);
    setError(null);
    setSuccessMessage(null);
    try {
      try {
        await sendPasswordResetEmail(auth, email.trim(), {
          url: window.location.origin,
          handleCodeInApp: true,
        });
      } catch (domainErr: any) {
        if (domainErr.code === 'auth/unauthorized-domain') {
          console.warn("Domain not authorized in Firebase yet. Retrying default email reset link...");
          await sendPasswordResetEmail(auth, email.trim());
        } else {
          throw domainErr;
        }
      }
      setSuccessMessage('Şifre sıfırlama e-postası gönderildi. Gelen kutunuzu (ve gereksiz kutusunu) kontrol edin.');
    } catch (err: any) {
      console.error(err);
      if (err.code === 'auth/user-not-found') {
        setError('Bu e-posta adresiyle kayıtlı bir kullanıcı bulunamadı.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Geçersiz bir e-posta adresi girdiniz.');
      } else {
        setError('Şifre sıfırlama e-postası gönderilirken hata oluştu: ' + (err.message || err.code));
      }
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F8F7] px-4">
      {/* Decorative blurred backgrounds */}
      <div className="absolute top-20 left-20 w-72 h-72 bg-stone-200/40 rounded-full filter blur-3xl opacity-50 pointer-events-none" />
      <div className="absolute bottom-20 right-20 w-80 h-80 bg-brand-100/20 rounded-full filter blur-3xl opacity-40 pointer-events-none" />

      <div className="w-full max-w-md bg-white p-8 rounded-xl shadow-card border border-stone-200/80 relative z-10">
        <div className="text-center space-y-3 mb-8 flex flex-col items-center">
          <img src="/intema_logo.png" alt="İntema Logo" className="h-28 object-contain mb-1" />
          <p className="text-[11px] text-stone-500 font-medium uppercase tracking-widest text-center px-4 leading-relaxed">
            Teklif Fiyatlandırma & Sunum Portalı
          </p>
        </div>

        {error && (
          <div className="mb-5 p-3.5 bg-red-50 border-l-2 border-red-500 rounded-lg text-red-700 text-xs flex items-start space-x-2 animate-in fade-in duration-200">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="mb-5 p-3.5 bg-emerald-50 border-l-2 border-emerald-500 rounded-lg text-emerald-700 text-xs flex items-start space-x-2 animate-in fade-in duration-200">
            <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{successMessage}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-medium text-stone-500 mb-1.5">
              E-posta Adresi
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
                <Mail className="w-4 h-4" />
              </span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ornek@example.com"
                className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-stone-200/90 rounded-lg focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 focus:outline-none transition-all text-stone-800"
              />
            </div>
          </div>

          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-[11px] font-medium text-stone-500">
                Şifre
              </label>
              <button
                type="button"
                disabled={resetLoading}
                onClick={handleForgotPassword}
                className="text-[10.5px] text-stone-400 hover:text-[#945f46] font-medium transition-colors disabled:opacity-50"
              >
                {resetLoading ? 'Gönderiliyor...' : 'Şifremi Unuttum'}
              </button>
            </div>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
                <Lock className="w-4 h-4" />
              </span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full pl-9 pr-4 py-2 text-xs bg-white border border-stone-200/90 rounded-lg focus:ring-1 focus:ring-brand-500/30 focus:border-brand-500 focus:outline-none transition-all text-stone-800"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#945f46] hover:bg-[#83523c] text-white text-xs font-semibold py-2.5 px-4 rounded-lg shadow-xs transition-colors flex items-center justify-center space-x-2 tracking-wide disabled:opacity-75 disabled:cursor-not-allowed mt-6"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span>Giriş Yapılıyor...</span>
              </>
            ) : (
              <>
                <LogIn className="w-4 h-4" />
                <span>Giriş Yap</span>
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
